import React, { useEffect, useState } from 'react';
import User from './User';
import Kpi from './Kpi';
import Api from './Api';
import Affiliate from './Affiliate';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  COLOR_BACKGROUND_1,
  COLOR_BACKGROUND_2,
  COLOR_GREY_200,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_TEXT_2,
} from 'src/utils/colors';
import {
  ADMIN_DASHBOARD_TIMEZONE_STORAGE_KEY,
  ADMIN_TIMEZONE_OPTIONS,
  DEFAULT_ADMIN_TIMEZONE,
} from '../adminDisplayTime';

type AdminTab = 'user' | 'kpi' | 'affiliate' | 'api';

const Admin = () => {
  const [dataType, setDataType] = useState<AdminTab>('user');
  const [showSearchbar, setShowSearchbar] = useState<boolean>(true);
  const [openPasswordModal, setOpenPasswordModal] = useState<boolean>(false);
  const [adminPassword, setAdminPassword] = useState<string | null>(() => localStorage.getItem('adminPassword'));
  const [displayTimeZone, setDisplayTimeZone] = useState<string>(() => {
    const saved = localStorage.getItem(ADMIN_DASHBOARD_TIMEZONE_STORAGE_KEY);
    if (saved && ADMIN_TIMEZONE_OPTIONS.some(o => o.value === saved)) return saved;
    return DEFAULT_ADMIN_TIMEZONE;
  });
  const _window: any = window;
  const Tawk_API = _window.Tawk_API || {};

  const closeChat = () => {
    Tawk_API && Tawk_API.hideWidget && Tawk_API.hideWidget();
  };

  useEffect(() => {
    closeChat();
  }, []);

  useEffect(() => {
    if (!adminPassword) {
      setOpenPasswordModal(true);
    }
  }, [adminPassword]);

  useEffect(() => {
    localStorage.setItem(ADMIN_DASHBOARD_TIMEZONE_STORAGE_KEY, displayTimeZone);
  }, [displayTimeZone]);

  const handleSaveAdminPassword = password => {
    localStorage.setItem('adminPassword', password);
    setAdminPassword(password);
    setOpenPasswordModal(false);
  };

  const ShowPasswordModal = () => {
    const [password, setPassword] = useState<string>('');
    return (
      <Modal open={openPasswordModal}>
        <Box
          sx={{
            width: 'min(480px, 92vw)',
            background: COLOR_BACKGROUND_2,
            position: 'absolute',
            top: '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 3,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: `1px solid ${COLOR_GREY_200}`,
          }}
        >
          <Typography variant="h6" sx={{ color: COLOR_TEXT_2, mb: 2 }}>
            Enter the admin dashboard password
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: 'stretch' }}>
            <TextField
              id="admin-password-field"
              variant="outlined"
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              sx={{ flex: 1 }}
              fullWidth
            />
            <Button
              variant="contained"
              sx={{
                backgroundColor: COLOR_PRIMARY_1,
                color: '#fff',
                px: 3,
                '&:hover': { backgroundColor: COLOR_PRIMARY_2 },
              }}
              onClick={() => handleSaveAdminPassword(password)}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  const outlineButtonSx = {
    color: COLOR_PRIMARY_2,
    borderColor: COLOR_PRIMARY_1,
    '&:hover': {
      borderColor: COLOR_PRIMARY_2,
      backgroundColor: 'rgba(3, 122, 144, 0.06)',
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: COLOR_BACKGROUND_1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        py: 2,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: 'min(1400px, 96vw)',
          minHeight: '90vh',
          backgroundColor: COLOR_BACKGROUND_2,
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          boxShadow: '0 8px 28px rgba(15, 23, 42, 0.08)',
          border: `1px solid rgba(0, 0, 0, 0.06)`,
        }}
      >
        <Grid container sx={{ width: '100%' }} alignItems="flex-start" spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
                pb: 2,
                borderBottom: `1px solid ${COLOR_GREY_200}`,
              }}
            >
              <Box sx={{ minWidth: 140 }}>
                {!showSearchbar && (
                  <Button variant="outlined" sx={outlineButtonSx} onClick={() => setShowSearchbar(true)}>
                    Back to search
                  </Button>
                )}
                {showSearchbar && dataType === 'user' && (
                  <Button variant="outlined" sx={outlineButtonSx} onClick={() => setOpenPasswordModal(true)}>
                    Reset password
                  </Button>
                )}
              </Box>

              <Tabs
                value={dataType}
                onChange={(_, v: AdminTab) => setDataType(v)}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    minHeight: 44,
                    color: COLOR_TEXT_2,
                    borderRadius: 1,
                  },
                  '& .Mui-selected': {
                    color: `${COLOR_PRIMARY_2} !important`,
                    backgroundColor: 'rgba(3, 122, 144, 0.08)',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: COLOR_PRIMARY_1,
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab label="User" value="user" />
                <Tab label="KPI" value="kpi" />
                <Tab label="Affiliate" value="affiliate" />
                <Tab label="API" value="api" />
              </Tabs>

              <FormControl size="small" sx={{ minWidth: 168 }}>
                <InputLabel id="admin-dashboard-tz-label">Display timezone</InputLabel>
                <Select
                  labelId="admin-dashboard-tz-label"
                  label="Display timezone"
                  value={displayTimeZone}
                  onChange={e => setDisplayTimeZone(String(e.target.value))}
                >
                  {ADMIN_TIMEZONE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>

        <ShowPasswordModal />

        {dataType === 'user' && (
          <User
            adminPassword={adminPassword}
            dataType={dataType}
            showSearchbar={showSearchbar}
            setShowSearchbar={setShowSearchbar}
            displayTimeZone={displayTimeZone}
          />
        )}
        {dataType === 'kpi' && <Kpi adminPassword={adminPassword} />}
        {dataType === 'affiliate' && <Affiliate adminPassword={adminPassword} />}
        {dataType === 'api' && <Api adminPassword={adminPassword} displayTimeZone={displayTimeZone} />}
      </Box>
    </Box>
  );
};

export default Admin;
